<template>
    <div class="py-2">
        <div class="flex items-center gap-4 mb-4">
            <!-- Search Bar -->
            <div class="relative flex-1 max-w-md">
                <input
                    type="text"
                    v-model="searchQuery"
                    :placeholder="$t('Search contacts...')"
                    class="w-full h-10 px-4 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                    v-if="searchQuery"
                    type="button"
                    @click="searchQuery = ''"
                    class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                    <i class="fas fa-times"></i>
                </button>
            </div>

            <!-- Type Filter -->
            <div class="relative type-filter-container">
                <button
                    type="button"
                    @click="typeFilterOpen = !typeFilterOpen"
                    class="w-64 h-10 px-4 text-left bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between"
                >
                    <span class="text-sm text-gray-700">
                        {{ selectedTypeFilters.length > 0 
                            ? `${selectedTypeFilters.length} ${selectedTypeFilters.length === 1 ? 'type' : 'types'} selected`
                            : 'Filter by type...' }}
                    </span>
                    <svg 
                        class="w-5 h-5 text-gray-400 transition-transform"
                        :class="{ 'transform rotate-180': typeFilterOpen }"
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                    >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                <!-- Dropdown -->
                <div
                    v-if="typeFilterOpen"
                    class="absolute z-50 mt-1 w-64 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-y-auto"
                >
                    <div class="p-2">
                        <div class="flex items-center justify-between mb-2 px-2">
                            <span class="text-xs font-medium text-gray-500 uppercase">Select Types</span>
                            <button
                                v-if="selectedTypeFilters.length > 0"
                                type="button"
                                @click="clearTypeFilters"
                                class="text-xs text-blue-600 hover:text-blue-800"
                            >
                                Clear All
                            </button>
                        </div>
                        <div class="space-y-0.5">
                            <label
                                v-for="typeOption in allContactTypes"
                                :key="typeOption.value"
                                class="flex items-center px-2 py-1 hover:bg-gray-50 rounded cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    :value="typeOption.value"
                                    :checked="selectedTypeFilters.includes(typeOption.value)"
                                    @change="toggleTypeFilter(typeOption.value)"
                                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                                />
                                <span class="text-sm text-gray-700">{{ typeOption.label }}</span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Preferred Vendor Filter -->
            <div class="flex items-center">
                <label class="flex items-center space-x-2 cursor-pointer">
                    <input
                        type="checkbox"
                        v-model="showPreferredVendorsOnly"
                        class="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                    />
                    <span class="text-sm text-gray-700">
                        {{ $t('Preferred Vendors') }}
                    </span>
                </label>
            </div>

            <div class="flex items-center gap-4 ml-auto">
                <!-- Export Button -->
                <div class="relative inline-block text-left" v-click-outside="closeExportMenu">
                    <button
                        type="button"
                        class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 h-10"
                        @click="showExportMenu = !showExportMenu"
                    >
                        Export
                        <i class="fa fa-chevron-down ml-2 text-xs"></i>
                    </button>

                    <div
                        v-if="showExportMenu"
                        class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10"
                    >
                        <div class="py-1">
                            <button
                                type="button"
                                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                @click="exportData('csv')"
                            >
                                Export as CSV
                            </button>
                            <button
                                type="button"
                                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                @click="exportData('xlsx')"
                            >
                                Export as Excel
                            </button>
                        </div>
                    </div>
                </div>

                <button
                    type="button"
                    class="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 h-10"
                    @click="openAddModal"
                >
                    <i class="fas fa-plus mr-2"></i>
                    {{ $t('Add Contact') }}
                </button>
            </div>
        </div>

        <!-- Contacts Table -->
        <div class="bg-white rounded-lg shadow overflow-hidden">
            <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                    <tr>
                        <th 
                            class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            @click="sortBy('name')"
                        >
                            <div class="flex items-center space-x-1">
                                <span>{{ $t('Name') }}</span>
                                <svg v-if="sortColumn === 'name'" 
                                     class="w-3 h-3 text-blue-600 -mt-0.5"
                                     fill="none" 
                                     stroke="currentColor" 
                                     viewBox="0 0 24 24"
                                >
                                    <path v-if="sortDirection === 'asc'"
                                          stroke-linecap="round" 
                                          stroke-linejoin="round" 
                                          stroke-width="2" 
                                          d="M5 15l7-7 7 7"
                                    />
                                    <path v-else
                                          stroke-linecap="round" 
                                          stroke-linejoin="round" 
                                          stroke-width="2" 
                                          d="M19 9l-7 7-7-7"
                                    />
                                </svg>
                                <span v-else class="w-3"></span>
                            </div>
                        </th>
                        <th 
                            class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            @click="sortBy('company')"
                        >
                            <div class="flex items-center space-x-1">
                                <span>{{ $t('Company') }}</span>
                                <svg v-if="sortColumn === 'company'" 
                                     class="w-3 h-3 text-blue-600 -mt-0.5"
                                     fill="none" 
                                     stroke="currentColor" 
                                     viewBox="0 0 24 24"
                                >
                                    <path v-if="sortDirection === 'asc'"
                                          stroke-linecap="round" 
                                          stroke-linejoin="round" 
                                          stroke-width="2" 
                                          d="M5 15l7-7 7 7"
                                    />
                                    <path v-else
                                          stroke-linecap="round" 
                                          stroke-linejoin="round" 
                                          stroke-width="2" 
                                          d="M19 9l-7 7-7-7"
                                    />
                                </svg>
                                <span v-else class="w-3"></span>
                            </div>
                        </th>
                        <th 
                            class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            @click="sortBy('email')"
                        >
                            <div class="flex items-center space-x-1">
                                <span>{{ $t('Email') }}</span>
                                <svg v-if="sortColumn === 'email'" 
                                     class="w-3 h-3 text-blue-600 -mt-0.5"
                                     fill="none" 
                                     stroke="currentColor" 
                                     viewBox="0 0 24 24"
                                >
                                    <path v-if="sortDirection === 'asc'"
                                          stroke-linecap="round" 
                                          stroke-linejoin="round" 
                                          stroke-width="2" 
                                          d="M5 15l7-7 7 7"
                                    />
                                    <path v-else
                                          stroke-linecap="round" 
                                          stroke-linejoin="round" 
                                          stroke-width="2" 
                                          d="M19 9l-7 7-7-7"
                                    />
                                </svg>
                                <span v-else class="w-3"></span>
                            </div>
                        </th>
                        <th 
                            class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            @click="sortBy('phone')"
                        >
                            <div class="flex items-center space-x-1">
                                <span>{{ $t('Phone') }}</span>
                                <svg v-if="sortColumn === 'phone'" 
                                     class="w-3 h-3 text-blue-600 -mt-0.5"
                                     fill="none" 
                                     stroke="currentColor" 
                                     viewBox="0 0 24 24"
                                >
                                    <path v-if="sortDirection === 'asc'"
                                          stroke-linecap="round" 
                                          stroke-linejoin="round" 
                                          stroke-width="2" 
                                          d="M5 15l7-7 7 7"
                                    />
                                    <path v-else
                                          stroke-linecap="round" 
                                          stroke-linejoin="round" 
                                          stroke-width="2" 
                                          d="M19 9l-7 7-7-7"
                                    />
                                </svg>
                                <span v-else class="w-3"></span>
                            </div>
                        </th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {{ $t('Types') }}
                        </th>
                        <th 
                            class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            @click="sortBy('deal_count')"
                        >
                            <div class="flex items-center space-x-1">
                                <span>{{ $t('Properties') }}</span>
                                <svg v-if="sortColumn === 'deal_count'" 
                                     class="w-3 h-3 text-blue-600 -mt-0.5"
                                     fill="none" 
                                     stroke="currentColor" 
                                     viewBox="0 0 24 24"
                                >
                                    <path v-if="sortDirection === 'asc'"
                                          stroke-linecap="round" 
                                          stroke-linejoin="round" 
                                          stroke-width="2" 
                                          d="M5 15l7-7 7 7"
                                    />
                                    <path v-else
                                          stroke-linecap="round" 
                                          stroke-linejoin="round" 
                                          stroke-width="2" 
                                          d="M19 9l-7 7-7-7"
                                    />
                                </svg>
                                <span v-else class="w-3"></span>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    <tr
                        v-for="contact in filteredContacts"
                        :key="contact.contact_id || contact.key"
                        class="hover:bg-gray-50 cursor-pointer"
                        @click="openDetailPanel(contact)"
                    >
                        <td class="px-6 py-2 whitespace-nowrap">
                            <div class="text-base text-gray-900 flex items-center gap-1"
                                :class="contact.preferred_vendor ? 'font-bold' : 'font-medium'"
                            >
                                {{ contact.name }}
                                <i v-if="contact.preferred_vendor" class="fas fa-star text-yellow-500 text-xs mb-1 ml-0.5" title="Preferred Vendor"></i>
                            </div>
                        </td>
                        <td class="px-6 py-2 whitespace-nowrap">
                            <div class="text-sm text-gray-500">
                                {{ contact.company || '—' }}
                            </div>
                        </td>
                        <td class="px-6 py-2 whitespace-nowrap">
                            <a
                                v-if="contact.email"
                                :href="'mailto:' + contact.email"
                                class="text-sm text-blue-600 hover:text-blue-800"
                                @click.stop
                            >
                                {{ contact.email }}
                            </a>
                            <span v-else class="text-sm text-gray-400">—</span>
                        </td>
                        <td class="px-6 py-2 whitespace-nowrap">
                            <span v-if="contact.phone" class="text-sm text-gray-900">
                                {{ contact.phone }}
                            </span>
                            <span v-else class="text-sm text-gray-400">—</span>
                        </td>
                        <td class="px-6 py-2">
                            <div class="flex flex-wrap gap-1">
                                <span
                                    v-for="type in contact.types.filter(t => t && String(t).trim())"
                                    :key="type"
                                    class="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800"
                                >
                                    {{ formatType(type) }}
                                </span>
                            </div>
                        </td>
                        <td class="px-6 py-2 whitespace-nowrap">
                            <span class="text-sm text-gray-600">
                                {{ contact.deal_count || 0 }} {{ (contact.deal_count || 0) === 1 ? $t('property') : $t('properties') }}
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>

            <div v-if="filteredContacts.length === 0" class="text-center py-12 text-gray-500">
                {{ searchQuery ? $t('No contacts match your search.') : $t('No contacts found.') }}
            </div>
        </div>

        <contact-form-modal
            ref="contactModal"
            @saved="handleContactSaved"
            @deleted="handleContactDeleted"
        />

        <contact-detail-panel
            :isOpen="detailPanelOpen"
            :contact_id="selectedContact?.contact_id"
            :contact_name="selectedContact?.name"
            :contact_data="selectedContactData"
            :properties="selectedContactProperties"
            @close="closeDetailPanel"
            @saved="handleContactSaved"
            @deleted="handleContactDeleted"
        />
    </div>
</template>

<script>
import { orderBy } from 'lodash';
import ContactFormModal from './contact-form-modal.vue';
import ContactDetailPanel from './contact-detail-panel.vue';

export default {
    components: {
        ContactFormModal,
        ContactDetailPanel,
    },

    props: {
        contacts: { default: () => [] },
        all_contacts: { default: () => [] },
    },

    data() {
        return {
            searchQuery: '',
            detailPanelOpen: false,
            selectedContact: null,
            selectedContactData: {},
            selectedContactProperties: [],
            sortColumn: 'name',
            sortDirection: 'asc',
            selectedTypeFilters: [],
            typeFilterOpen: false,
            showExportMenu: false,
            showPreferredVendorsOnly: false,
        }
    },

    computed: {
        contacts_grouped() {
            // Filter out contacts without names AND without email/phone
            const contactsWithNames = this.contacts.filter(contact => {
                const hasName = contact.name && contact.name.trim();
                const hasEmailOrPhone = (contact.email && contact.email.trim()) || (contact.phone && contact.phone.trim());
                return hasName && hasEmailOrPhone;
            });
            
            // Group by contact_id if available, otherwise by name+email+phone combination (for backward compatibility)
            const grouped = {};
            
            contactsWithNames.forEach(contact => {
                // Use contact_id as primary key if available (groups all deal assignments for same contact)
                // Otherwise, use name+email+phone combination to group orphaned contacts
                let key;
                if (contact.contact_id) {
                    key = `contact_${contact.contact_id}`;
                } else {
                    // For orphaned contacts, create a unique key from name + email + phone
                    // This ensures contacts with same name but different contact info are grouped separately
                    const nameKey = (contact.name || '').trim().toLowerCase();
                    const emailKey = (contact.email || '').trim().toLowerCase();
                    const phoneValue = contact.phone || '';
                    const phoneKey = phoneValue ? String(phoneValue).trim().replace(/\D/g, '') : ''; // Remove non-digits for comparison
                    key = `orphaned_${nameKey}_${emailKey}_${phoneKey}`;
                }
                
                if (!grouped[key]) {
                    grouped[key] = {
                        contact_id: contact.contact_id,
                        name: contact.name,
                        company: contact.company,
                        email: contact.email,
                        phone: contact.phone,
                        address_1: contact.address_1,
                        address_2: contact.address_2,
                        city: contact.city,
                        state: contact.state,
                        zip: contact.zip,
                        preferred_vendor: contact.preferred_vendor || false,
                        types: new Set(), // Track unique types
                        deals: [],
                    };
                    
                    // If we have a contact_id, look up the contact in all_contacts to get its types
                    if (contact.contact_id && this.all_contacts && this.all_contacts.length > 0) {
                        const fullContact = this.all_contacts.find(c => c.id === contact.contact_id);
                        if (fullContact) {
                            // Update preferred_vendor from all_contacts if available
                            if (fullContact.preferred_vendor !== undefined) {
                                grouped[key].preferred_vendor = fullContact.preferred_vendor;
                            }
                        }
                        if (fullContact && fullContact.types) {
                            let typesToAdd = [];
                            
                            // Handle both array of strings and array of objects
                            if (Array.isArray(fullContact.types)) {
                                typesToAdd = fullContact.types.map(t => {
                                    // If it's an object with a 'type' property, extract it
                                    if (typeof t === 'object' && t !== null && t.type) {
                                        return t.type;
                                    }
                                    // Otherwise, treat it as a string
                                    return t;
                                });
                            }
                            
                            // Add each type to the Set
                            typesToAdd.forEach(type => {
                                const typeStr = String(type || '').trim();
                                if (typeStr) {
                                    grouped[key].types.add(typeStr);
                                }
                            });
                        }
                    }
                }
                
                // Add deal assignment
                if (contact.deal) {
                    grouped[key].deals.push({
                        id: contact.id,
                        type: contact.type,
                        deal: contact.deal,
                    });
                    // Only add type if it's not empty/null/undefined
                    if (contact.type && contact.type.trim && contact.type.trim()) {
                        grouped[key].types.add(contact.type);
                    } else if (contact.type && String(contact.type).trim()) {
                        grouped[key].types.add(contact.type);
                    }
                }
                
                // Also add Contact's types (from contact_types table) if available on this contact item
                // This is a fallback in case all_contacts lookup didn't work
                if (contact.contact_types) {
                    let typesToAdd = [];
                    
                    // Handle both array of strings and array of objects
                    if (Array.isArray(contact.contact_types)) {
                        typesToAdd = contact.contact_types.map(t => {
                            // If it's an object with a 'type' property, extract it
                            if (typeof t === 'object' && t !== null && t.type) {
                                return t.type;
                            }
                            // Otherwise, treat it as a string
                            return t;
                        });
                    }
                    
                    // Add each type to the Set
                    typesToAdd.forEach(type => {
                        const typeStr = String(type || '').trim();
                        if (typeStr) {
                            grouped[key].types.add(typeStr);
                        }
                    });
                }
            });
            
            // Convert Set to Array, filter out empty values, and sort
            return orderBy(Object.values(grouped).map(contact => {
                return {
                    ...contact,
                    types: Array.from(contact.types).filter(t => t && String(t).trim()).sort(),
                    deal_count: contact.deals.length,
                    preferred_vendor: contact.preferred_vendor || false,
                };
            }), 'name', 'asc');
        },

        filteredContacts() {
            const contacts = this.contacts_grouped.filter(contact => {
                if (!contact.name || !contact.name.trim()) {
                    return false;
                }

                // Filter by search query
                if (this.searchQuery) {
                    const query = this.searchQuery.toLowerCase();
                    const matchesSearch = (
                        contact.name.toLowerCase().includes(query) ||
                        (contact.company && contact.company.toLowerCase().includes(query)) ||
                        (contact.email && contact.email.toLowerCase().includes(query)) ||
                        (contact.phone && contact.phone.includes(query))
                    );
                    if (!matchesSearch) {
                        return false;
                    }
                }

                // Filter by selected types
                if (this.selectedTypeFilters.length > 0) {
                    const contactTypes = contact.types || [];
                    // Get all type values to check (handles grouped types like buyer_1/buyer_2)
                    const filterTypeValues = this.selectedTypeFilters.flatMap(filterValue => {
                        const typeOption = this.allContactTypes.find(t => t.value === filterValue);
                        return typeOption ? typeOption.values : [filterValue];
                    });
                    const hasMatchingType = filterTypeValues.some(filterType => 
                        contactTypes.includes(filterType)
                    );
                    if (!hasMatchingType) {
                        return false;
                    }
                }

                // Filter by preferred vendor
                if (this.showPreferredVendorsOnly) {
                    if (!contact.preferred_vendor) {
                        return false;
                    }
                }

                return true;
            });

            return orderBy(contacts, [this.sortColumn], [this.sortDirection]);
        },

        allContactTypes() {
            // Get all contact types, alphabetized by display name
            // Group buyer_1/buyer_2 and seller_1/seller_2 together since they have the same label
            const types = [
                { value: 'buyer', label: 'Buyer', values: ['buyer_1', 'buyer_2'] },
                { value: 'future_buyer', label: 'Future Buyer', values: ['future_buyer'] },
                { value: 'buyer_attorney', label: 'Buyer Attorney', values: ['buyer_attorney'] },
                { value: 'buyer_broker', label: 'Buyer Broker', values: ['buyer_broker'] },
                { value: 'buyer_contact', label: 'Buyer Contact', values: ['buyer_contact'] },
                { value: 'buyer_lender', label: 'Buyer Lender', values: ['buyer_lender'] },
                { value: 'buyer_mortgage_broker', label: 'Buyer Mortgage Broker', values: ['buyer_mortgage_broker'] },
                { value: 'buyer_real_estate_agent', label: 'Buyer Real Estate Agent', values: ['buyer_real_estate_agent'] },
                { value: 'closing_agent', label: 'Closing Agent', values: ['closing_agent'] },
                { value: 'inspector', label: 'Inspector', values: ['inspector'] },
                { value: 'insurance_agent', label: 'Insurance Agent', values: ['insurance_agent'] },

                { value: 'seller', label: 'Seller', values: ['seller_1', 'seller_2'] },
                { value: 'future_seller', label: 'Future Seller', values: ['future_seller'] },
                { value: 'seller_attorney', label: 'Seller Attorney', values: ['seller_attorney'] },
                { value: 'seller_broker', label: 'Seller Broker', values: ['seller_broker'] },

                { value: 'home_office_coordinator', label: 'Home Office Coordinator', values: ['home_office_coordinator'] },
                { value: 'managing_broker', label: 'Managing Broker', values: ['managing_broker'] },
                { value: 'office_manager', label: 'Office Manager', values: ['office_manager'] },
                { value: 'transaction_coordinator', label: 'Transaction Coordinator', values: ['transaction_coordinator'] },
            ];
            
            return types;
        },
    },

    methods: {
        sortBy(column) {
            if (this.sortColumn === column) {
                // Toggle direction if clicking the same column
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
            } else {
                // Set new column and default to ascending
                this.sortColumn = column;
                this.sortDirection = 'asc';
            }
        },
        openAddModal() {
            // Open panel in "add" mode
            this.selectedContact = null;
            this.selectedContactData = {};
            this.selectedContactProperties = [];
            this.detailPanelOpen = true;
        },
        
        openDetailPanel(contact) {
            // Find full contact data from all_contacts if available
            let contactData = {};
            if (contact.contact_id) {
                const fullContact = this.all_contacts.find(c => c.id === contact.contact_id);
                if (fullContact) {
                    contactData = {
                        contact_id: fullContact.id,
                        name: fullContact.name,
                        company: fullContact.company,
                        email: fullContact.email,
                        phone: fullContact.phone,
                        address_1: fullContact.address_1,
                        address_2: fullContact.address_2,
                        city: fullContact.city,
                        state: fullContact.state,
                        zip: fullContact.zip,
                        preferred_vendor: fullContact.preferred_vendor || false,
                        types: Array.isArray(fullContact.types) ? fullContact.types.map(t => t.type || t) : (fullContact.types || []),
                    };
                } else {
                    // Fallback to grouped contact data
                    contactData = {
                        contact_id: contact.contact_id,
                        name: contact.name,
                        company: contact.company,
                        email: contact.email,
                        phone: contact.phone,
                        address_1: contact.address_1,
                        address_2: contact.address_2,
                        city: contact.city,
                        state: contact.state,
                        zip: contact.zip,
                        preferred_vendor: contact.preferred_vendor || false,
                        types: contact.types || [],
                    };
                }
            } else {
                // For orphaned contacts, use grouped data
                contactData = {
                    contact_id: null,
                    name: contact.name,
                    company: contact.company,
                    email: contact.email,
                    phone: contact.phone,
                    address_1: contact.address_1,
                    address_2: contact.address_2,
                    city: contact.city,
                    state: contact.state,
                    zip: contact.zip,
                    preferred_vendor: contact.preferred_vendor || false,
                    types: contact.types || [],
                };
            }

            const properties = (contact.deals || []).map(dealAssignment => {
                // Handle different possible structures of deal data
                const deal = dealAssignment.deal;
                if (!deal) {
                    return null;
                }
                
                // Property should be nested in deal.property (from DealResource)
                // DealResource structure: { id: deal.id, property: PropertyResource }
                // PropertyResource has: id, address, city, state, zip, mls_number, etc.
                const property = deal.property;
                
                if (!property || !property.id) {
                    return null;
                }
                
                return {
                    id: property.id,
                    mls_number: property.mls_number || null,
                    type: dealAssignment.type,
                    property: property,
                };
            }).filter(p => p !== null);

            this.selectedContact = contact;
            this.selectedContactData = contactData;
            this.selectedContactProperties = properties;
            this.detailPanelOpen = true;
        },

        closeDetailPanel() {
            this.detailPanelOpen = false;
            this.selectedContact = null;
            this.selectedContactData = {};
            this.selectedContactProperties = [];
        },
        handleContactSaved(contact) {
            // Reload the page to refresh the contact list
            window.location.reload();
        },
        handleContactDeleted(contactId) {
            // Reload the page to refresh the contact list
            window.location.reload();
        },
        formatAddress(object) {
            if (object) {
                return [
                    object.address || object.address_1,
                    object.address_2,
                    object.city,
                    [object.state, object.zip].join(' '),
                ].filter(value => value && value.trim())
                .join(', ');
            }
            return '';
        },
        formatType(type) {
            if (!type) return '';
            
            const typeMap = {
                'buyer_1': 'Buyer',
                'buyer_2': 'Buyer',
                'seller_1': 'Seller',
                'seller_2': 'Seller',
                'closing_agent': 'Closing Agent',
                'home_office_coordinator': 'Home Office Coordinator',
                'managing_broker': 'Managing Broker',
                'office_manager': 'Office Manager',
                'transaction_coordinator': 'Transaction Coordinator',
                'seller_broker': 'Seller Broker',
                'buyer_broker': 'Buyer Broker',
                'seller_attorney': 'Seller Attorney',
                'buyer_lender': 'Buyer Lender',
                'inspector': 'Inspector',
                'buyer_mortgage_broker': 'Buyer Mortgage Broker',
                'buyer_attorney': 'Buyer Attorney',
                'buyer_real_estate_agent': 'Buyer Real Estate Agent',
                'broker_contact': 'Broker Contact',
                'buyer_contact': 'Buyer Contact',
                'insurance_agent': 'Insurance Agent',
            };
            
            return typeMap[type] || this.titleCase(type);
        },
        titleCase(str) {
            if (!str) return '';
            return String(str).replace(/^[-_]*(.)/, (_, c) => c.toUpperCase())
                .replace(/[-_]+(.)/g, (_, c) => ' ' + c.toUpperCase());
        },
        toggleTypeFilter(typeValue) {
            const index = this.selectedTypeFilters.indexOf(typeValue);
            if (index > -1) {
                this.selectedTypeFilters.splice(index, 1);
            } else {
                this.selectedTypeFilters.push(typeValue);
            }
        },
        clearTypeFilters() {
            this.selectedTypeFilters = [];
        },
        closeTypeFilter() {
            this.typeFilterOpen = false;
        },
        closeExportMenu() {
            this.showExportMenu = false;
        },
        exportData(format) {
            this.showExportMenu = false;
            
            // Build export URL with current filters and search
            const params = new URLSearchParams({
                format: format,
                sort_column: this.sortColumn,
                sort_direction: this.sortDirection,
            });
            
            if (this.searchQuery) {
                params.append('search', this.searchQuery);
            }
            
            if (this.selectedTypeFilters.length > 0) {
                this.selectedTypeFilters.forEach(type => {
                    params.append('types[]', type);
                });
            }
            
            if (this.showPreferredVendorsOnly) {
                params.append('preferred_vendor_only', '1');
            }

            const url = `/broker/contacts/export?${params.toString()}`;
            
            // Create a temporary link and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = '';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
    },

    mounted() {
        // Close dropdowns when clicking outside
        this.handleClickOutside = (e) => {
            const filterContainer = this.$el.querySelector('.type-filter-container');
            if (this.typeFilterOpen && filterContainer && !filterContainer.contains(e.target)) {
                this.typeFilterOpen = false;
            }
            
            // Close export menu when clicking outside
            if (this.showExportMenu && !e.target.closest('.relative.inline-block')) {
                this.showExportMenu = false;
            }
        };
        document.addEventListener('click', this.handleClickOutside);
    },

    beforeDestroy() {
        if (this.handleClickOutside) {
            document.removeEventListener('click', this.handleClickOutside);
        }
    },
}
</script>

